import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Loader2, Copy } from 'lucide-react';
import { api } from '@/lib/api';
import type { MealSession, VenueArea, SessionHours } from '@/types';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

interface HourEntry {
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
  lastBookingTime: string;
  isClosed: boolean;
}

export default function SessionHoursForm() {
  const { venueId, areaId, sessionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [hours, setHours] = useState<HourEntry[]>(
    DAYS_OF_WEEK.map((day) => ({
      dayOfWeek: day.value,
      openTime: '12:00',
      closeTime: '22:00',
      lastBookingTime: '21:00',
      isClosed: false,
    }))
  );

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: area } = useQuery<VenueArea>({
    queryKey: ['venue-area', venueId, areaId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}`);
      return response.data;
    },
  });

  const { data: session } = useQuery<MealSession>({
    queryKey: ['area-session', venueId, areaId, sessionId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}/sessions/${sessionId}`);
      return response.data;
    },
  });

  const { data: existingHours, isLoading } = useQuery<SessionHours[]>({
    queryKey: ['session-hours', venueId, areaId, sessionId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}/sessions/${sessionId}/hours`);
      return response.data;
    },
  });

  useEffect(() => {
    if (existingHours && existingHours.length > 0) {
      const newHours = DAYS_OF_WEEK.map((day) => {
        const existing = existingHours.find((h) => h.day_of_week === day.value);
        if (existing) {
          return {
            dayOfWeek: existing.day_of_week,
            openTime: existing.open_time || '12:00',
            closeTime: existing.close_time || '22:00',
            lastBookingTime: existing.last_booking_time || '21:00',
            isClosed: existing.is_closed,
          };
        }
        return {
          dayOfWeek: day.value,
          openTime: '12:00',
          closeTime: '22:00',
          lastBookingTime: '21:00',
          isClosed: false,
        };
      });
      setHours(newHours);
    }
  }, [existingHours]);

  const saveMutation = useMutation({
    mutationFn: async (data: HourEntry[]) => {
      await api.put(`/admin/venues/${venueId}/areas/${areaId}/sessions/${sessionId}/hours`, {
        hours: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-hours', venueId, areaId, sessionId] });
      navigate(`/venues/${venueId}/booking-config/areas/${areaId}/sessions`);
    },
  });

  const updateHour = (dayOfWeek: number, field: keyof HourEntry, value: string | boolean) => {
    setHours((prev) =>
      prev.map((h) =>
        h.dayOfWeek === dayOfWeek ? { ...h, [field]: value } : h
      )
    );
  };

  const applyToWeekdays = () => {
    const monday = hours.find((h) => h.dayOfWeek === 1);
    if (!monday) return;
    setHours((prev) =>
      prev.map((h) =>
        h.dayOfWeek >= 1 && h.dayOfWeek <= 5
          ? { ...h, openTime: monday.openTime, closeTime: monday.closeTime, lastBookingTime: monday.lastBookingTime, isClosed: monday.isClosed }
          : h
      )
    );
  };

  const applyToWeekends = () => {
    const saturday = hours.find((h) => h.dayOfWeek === 6);
    if (!saturday) return;
    setHours((prev) =>
      prev.map((h) =>
        h.dayOfWeek === 0 || h.dayOfWeek === 6
          ? { ...h, openTime: saturday.openTime, closeTime: saturday.closeTime, lastBookingTime: saturday.lastBookingTime, isClosed: saturday.isClosed }
          : h
      )
    );
  };

  const applyToAll = () => {
    const first = hours[0];
    setHours((prev) =>
      prev.map((h) => ({
        ...h,
        openTime: first.openTime,
        closeTime: first.closeTime,
        lastBookingTime: first.lastBookingTime,
        isClosed: first.isClosed,
      }))
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(hours);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/booking-config/areas/${areaId}/sessions`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Session Hours</h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string} - {area?.name} - {session?.name}
          </p>
        </div>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          {saveMutation.error && (
            <div className="p-4 bg-red-50 border-b border-red-200 text-red-700 text-sm">
              {(saveMutation.error as Error)?.message || 'An error occurred'}
            </div>
          )}

          {/* Quick Apply Buttons */}
          <div className="p-4 bg-gray-50 border-b flex flex-wrap gap-2">
            <span className="text-sm text-gray-600 mr-2">Quick Apply:</span>
            <button
              type="button"
              onClick={applyToAll}
              className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy Sunday to All Days
            </button>
            <button
              type="button"
              onClick={applyToWeekdays}
              className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy Monday to Weekdays
            </button>
            <button
              type="button"
              onClick={applyToWeekends}
              className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy Saturday to Weekends
            </button>
          </div>

          {/* Hours Grid */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Day
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Closed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Open Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Close Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Booking
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {DAYS_OF_WEEK.map((day) => {
                  const hourEntry = hours.find((h) => h.dayOfWeek === day.value);
                  if (!hourEntry) return null;

                  return (
                    <tr
                      key={day.value}
                      className={`${hourEntry.isClosed ? 'bg-gray-50 opacity-60' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-900">{day.label}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={hourEntry.isClosed}
                          onChange={(e) => updateHour(day.value, 'isClosed', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="time"
                          value={hourEntry.openTime}
                          onChange={(e) => updateHour(day.value, 'openTime', e.target.value)}
                          disabled={hourEntry.isClosed}
                          className="input w-32"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="time"
                          value={hourEntry.closeTime}
                          onChange={(e) => updateHour(day.value, 'closeTime', e.target.value)}
                          disabled={hourEntry.isClosed}
                          className="input w-32"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="time"
                          value={hourEntry.lastBookingTime}
                          onChange={(e) => updateHour(day.value, 'lastBookingTime', e.target.value)}
                          disabled={hourEntry.isClosed}
                          className="input w-32"
                        />
                        <p className="text-xs text-gray-500 mt-1">No bookings after this time</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(`/venues/${venueId}/booking-config/areas/${areaId}/sessions`)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="btn btn-primary"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Hours
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Info Card */}
      <div className="mt-6 card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Operating Hours Guide</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-700">Open Time</p>
            <p className="text-gray-500">When this session begins accepting bookings.</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Close Time</p>
            <p className="text-gray-500">When this session ends (kitchen closes).</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Last Booking</p>
            <p className="text-gray-500">The latest time a new booking can start. Set earlier than close time to ensure guests can complete their meal.</p>
          </div>
          <div>
            <p className="font-medium text-gray-700">Closed Days</p>
            <p className="text-gray-500">Check "Closed" for days when this session is not available.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
